import { ReviewData } from '@aidkitorg/aidkit/lib/distro/review/distroReview';
import {
  Button,
  Heading,
  Tooltip,
  useToast
} from '@aidkitorg/component-library';
import { useMemo, useState } from 'react';
import { usePost } from '../API';

interface DistroReviewSummaryProps {
  reviewData: ReviewData | null;
  reviewId: string;
  isClosing: boolean;
  isPublishing: boolean;
  onCloseReviewClick: () => void;
  onUpdate?: () => void;
  surveyHashMismatch?: boolean;
}

const getWorkflowTooltip = (workflowName: string): string => {
  switch (workflowName) {
    case 'sample_applicants':
      return 'Picks the (typically random-sampled) applicant population the other workflows run against. Used by other workflows.';
    case 'data_snapshot':
      return 'Creates a snapshot of current applicant data for the sampled applicants. Used by other workflows.';
    case 'data_snapshot_simulated':
      return 'Creates another snapshot for the roboscreener simulation to act upon. Used by other workflows.';
    case 'nudge_data_snapshot':
      return 'Snapshots sent nudges and pending info for notification recipient tracking.';
    case 'roboscreener_simulation':
      return 'Runs a roboscreener simulation against the sampled snapshot. Used by other workflows.';
    case 'delta_roboscreener_simulation':
      return 'Runs a fast roboscreener simulation against live applicant data for every applicant, snapshotting only the values it would change. Used by other workflows.';
    case 'notifications':
      return 'Analyzes notification changes and calculates their impact on applicants, based on the sampled simulation.';
    case 'delta_notifications':
      return 'Diffs notification and follow-up definitions between the old and new survey.';
    case 'changes_v2':
      return 'Compares the old and new survey versions to identify all differences between them.';
    case 'target_fields':
      return 'Analyzes how survey definition changes affect applicants\' target field values, across the sampled population.';
    case 'delta_target_fields':
      return 'Analyzes how survey definition changes affect applicants\' target field values, covering only the fields the simulation would change.';
    case 'data_snapshot_cleanup':
      return 'Cleans up temporary data snapshots and related tables after all other workflows have completed.';
    default:
      return 'Unknown workflow.';
  }
};

export default function DistroReviewSummary({ 
  reviewData, 
  reviewId, 
  isClosing, 
  isPublishing, 
  onCloseReviewClick,
  onUpdate,
  surveyHashMismatch
}: DistroReviewSummaryProps) {
  const isOpen = reviewData?.status === 'review_pending' || reviewData?.status === 'approved';
  const { toast } = useToast();
  const rerunDistroReviewFull = usePost('/distro-review/rerun-full');
  const [isRerunningFull, setIsRerunningFull] = useState(false);

  const handleRunFullSimulation = async () => {
    setIsRerunningFull(true);
    try {
      const result = await rerunDistroReviewFull({ reviewId });
      if (result && 'error' in result) {
        toast({
          description: result.error ?? 'Failed to start full simulation',
          variant: 'error'
        });
      } else {
        toast({
          description: 'Full simulation started. Workflow statuses will update as it runs.',
          variant: 'success',
          duration: 5000
        });
        onUpdate?.();
      }
    } catch (error) {
      console.error('Error starting full simulation:', error);
      toast({
        description: 'Failed to start full simulation',
        variant: 'error'
      });
    } finally {
      setIsRerunningFull(false);
    }
  };

  const workflows = useMemo(() =>
    reviewData?.workflowStatuses
      ?.map((workflow) => ({
        ...workflow,
        durationInSeconds: workflow.completedAt && workflow.startedAt
          ? ((new Date(workflow.completedAt).getTime() - new Date(workflow.startedAt).getTime()) / 1000).toFixed(1)
          : null,
      })) || [],
  [reviewData]);

  return (
    <>
      <div className="flex flex-row justify-between items-start">
        <Heading level="h2" className="text-lg font-semibold">Summary</Heading>
        {isOpen && <Button
          onClick={onCloseReviewClick}
          isDisabled={isClosing || isPublishing}
          loading={isClosing}
          variant="danger"
        >
          Close Review
        </Button>
        }
      </div>
      {reviewData?.sampleInfo && (
        <div className="text-sm text-gray-600 mb-1">
          {reviewData.sampleInfo.isFullPopulation
            ? <>Simulated against the full population of <span className="font-medium">{reviewData.sampleInfo.programApplicantCount.toLocaleString()}</span> applicants.</>
            : <>
              Simulated using a random sample of <span className="font-medium">{reviewData.sampleInfo.sampledApplicantCount.toLocaleString()}</span> of{' '}
              <span className="font-medium">{reviewData.sampleInfo.programApplicantCount.toLocaleString()}</span> applicants in the program
              {' '}({reviewData.sampleInfo.programApplicantCount > 0 ? Math.round((reviewData.sampleInfo.sampledApplicantCount / reviewData.sampleInfo.programApplicantCount) * 100) : 0}%).
            </>
          }
        </div>
      )}
      <div className="text-sm text-gray-500">
        Created by {reviewData?.createdBy} on {reviewData?.createdAt && new Date(reviewData.createdAt).toLocaleString()}
      </div>

      {reviewData?.workflowStatuses && (
        <div className="mt-2 bg-white border border-gray-200 rounded-lg p-4 shadow-sm space-y-2 max-w-4xl">
          <Heading level="h3" className="text-base font-semibold text-gray-900 mb-4">Workflow Statuses</Heading>
          <table className="min-w-full">
            <thead>
              <tr className="border-b">
                <th className="text-left text-sm font-medium text-gray-900 py-2">Workflow</th>
                <th className="text-left text-sm font-medium text-gray-900 py-2 px-3">Status</th>
                <th className="text-left text-sm font-medium text-gray-900 py-2">Duration</th>
              </tr>
            </thead>
            <tbody>
              {workflows.map((workflow) => (
                <tr key={workflow.workflowName} className="border-b last:border-b-0">
                  <td className="py-2 text-sm font-medium text-gray-900">
                    <Tooltip text={getWorkflowTooltip(workflow.workflowName)} side="right">
                      <span className="cursor-help border-b border-dotted border-gray-400">
                        {workflow.workflowName}
                      </span>
                    </Tooltip>
                  </td>
                  <td className="py-2 px-3">
                    {workflow.status === 'failed' ? (
                      <span className="text-sm text-red-500 whitespace-pre-wrap">
                        {workflow.status}{workflow.error ? `: ${workflow.error}` : ''}
                      </span>
                    ) : workflow.status === 'completed' && workflow.warning ? (
                      <span className="text-sm text-orange-600 whitespace-pre-wrap">{workflow.status} with warning: {workflow.warning}</span>
                    ) : workflow.status === 'completed' ? (
                      <span className="text-sm text-green-600 whitespace-pre-wrap">{workflow.status}</span>
                    ) : (
                      <span className="text-sm text-gray-700 whitespace-pre-wrap">{workflow.status}</span>
                    )}
                  </td>
                  <td className="py-2 text-sm text-gray-700 whitespace-pre-wrap">
                    {workflow.durationInSeconds ? `${workflow.durationInSeconds} s` : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {isOpen && (
            <div className="pt-2">
              <Tooltip text="Re-runs this review's workflows against every applicant instead of the default random sample. Creates a new commit rather than overwriting the results above." side="right">
                <Button
                  onClick={handleRunFullSimulation}
                  isDisabled={isRerunningFull || isClosing || isPublishing}
                  loading={isRerunningFull}
                  variant="secondary"
                >
                  Run Full Simulation
                </Button>
              </Tooltip>
            </div>
          )}
        </div>
      )}
    </>
  );
}
